import { useState } from 'react'
import { BookOpen, Lock, Save } from 'lucide-react'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { toast } from 'sonner'
import { EXAMPLE_SCENARIO_DOCUMENT } from '../exampleScenario'

export const MIN_SCENARIO_CHARS = 100
export const MAX_TEXT_CHARS = 100_000
export const SETUP_LOCKED =
  'Locked: rounds have been played on this world. To change it, start the world over on the World page.'

interface Props {
  text: string
  saved: string
  onChange: (text: string) => void
  locked: boolean
  lockedReason: string
  onSave: (scenario: string) => Promise<boolean>
}

export default function ScenarioPhase({
  text,
  saved,
  onChange,
  locked,
  lockedReason,
  onSave,
}: Props) {
  const [saving, setSaving] = useState(false)
  const dirty = text !== saved

  const save = async () => {
    setSaving(true)
    if (await onSave(text)) toast.success('Scenario saved')
    setSaving(false)
  }

  return (
    <Card className='bg-white shadow-md'>
      <CardHeader>
        <CardTitle className='text-lg text-dark-blue'>1. Scenario</CardTitle>
        <CardDescription>
          The text the world is built from. Name the teams, what each one can do
          and wants, how one team's actions reach another, and the time horizon.
          The richer it is, the better the graph.
        </CardDescription>
      </CardHeader>
      <CardContent className='space-y-3'>
        <Textarea
          aria-label='Scenario document'
          className='min-h-[260px] font-mono text-sm'
          value={text}
          maxLength={MAX_TEXT_CHARS}
          readOnly={locked}
          onChange={(e) => onChange(e.target.value)}
          placeholder='Paste the scenario here.'
        />
        <div className='flex flex-wrap items-center justify-between gap-3'>
          <span className='text-xs text-muted-foreground'>
            {text.trim().length.toLocaleString()} characters
            {text.trim().length < MIN_SCENARIO_CHARS &&
              ` (at least ${MIN_SCENARIO_CHARS} needed)`}
          </span>
          {locked ? (
            <span className='inline-flex items-center gap-1 text-xs text-muted-foreground'>
              <Lock className='h-3.5 w-3.5' aria-hidden />
              {lockedReason}
            </span>
          ) : (
            <div className='flex gap-2'>
              <Button
                variant='outline'
                size='sm'
                onClick={() => onChange(EXAMPLE_SCENARIO_DOCUMENT)}
              >
                <BookOpen className='h-3.5 w-3.5' aria-hidden />
                Load example scenario
              </Button>
              <Button
                size='sm'
                className='bg-dark-blue hover:bg-dark-blue-hovered'
                disabled={!dirty || saving}
                onClick={save}
              >
                <Save className='h-3.5 w-3.5' aria-hidden />
                Save scenario
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
